import { Copy, Flex, Table, TableAction, Tag, Typography, useMessagedAsyncRetry } from "@payping/web-components";
import { TransactionFilter } from "@/components/DashReport/TransactionTabFilter";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { useAppDispatch, useAppSelector } from "@/utils/redux";
import { useState } from "react";
import { getAdvancedTransactions } from "@payping/redux/actions";
import { dateFormats, toJalali, toUtc, utcNow } from "@payping/utils/dateUtils";
import { getCorrectDate } from "@/utils/getCorrectDate";
import { PaymentStatusEnum } from "@payping/api";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import type { ColumnsType } from "@payping/web-components/components/table/types";
import TransactionDetails from "../TransactionDetails";
import { CuToman } from "@payping/icons";
import qs from "query-string";
import { useEnumTranslation } from "@/utils/useEnumTranslation";
import { NumberListParamsType, StringListParamsType } from "./types";
import { submitQueryParams } from "@payping/utils/query-string";
import { useLocation, useNavigate } from "react-router-dom";

export interface TransactionRowType {
  id?: number;
  amount?: number;
  code?: string;
  payDate?: string;
  payerName?: string;
  status?: PaymentStatusEnum;
  clientRefId?: string;
  key?: number;
  ipgType?: number;
}

// eslint-disable-next-line react-refresh/only-export-components
export const transactionFiltersSearchVariant = [
  "payerName",
  "payerIdentity",
  "code",
  "refId",
  "clientRefId",
  "ipgRefId",
  "cardNo",
  "searchByType",
] as const;

const ShowAllTransactionDetails = () => {
  const [detailPaymentCode, setDetailPaymentCode] = useState<string>();
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { getPaymentStatus } = useEnumTranslation();

  const paymentList = useAppSelector((state) => state.reports.list);
  const hasMore = useAppSelector((state) => state.reports.hasMore);

  // search params
  const parsedSearch = qs.parse(location.search, { arrayFormat: "bracket" });
  const {
    pageNumber = 1,
    pageSize = 10,
    paymentStatuses,
    amount,
    ipgTypes,
  }: Partial<NumberListParamsType> = qs.parse(location.search, { parseNumbers: true, arrayFormat: "bracket" });
  const {
    cardNo,
    payerName,
    payerIdentity,
    clientId,
    refId,
    ipgRefId,
    clientRefId,
    toDate,
    fromDate,
    codes,
  }: Partial<StringListParamsType> = qs.parse(location.search, {
    arrayFormat: "bracket",
  });

  const { loading, retry } = useMessagedAsyncRetry(
    async () => {
      const fromDateRange =
        (fromDate
          ? toUtc({ date: fromDate, dateFormat: dateFormats.standard.utcDateTime }).string
          : utcNow().object.add(-1, "months").format(dateFormats.standard.utcDateTime)) ?? null;
      const toDateRange =
        (toDate ? toUtc({ date: toDate, dateFormat: dateFormats.standard.utcDateTime }).string : utcNow().string) ??
        null;
      return await dispatch(
        getAdvancedTransactions({
          offset: (pageNumber - 1) * pageSize,
          limit: pageSize,
          code: codes,
          payerName,
          payerIdentity,
          clientId,
          refId,
          ipgRefId,
          clientRefId,
          minAmount: amount,
          maxAmount: amount,
          paymentStatus: paymentStatuses !== undefined ? paymentStatuses : [PaymentStatusEnum.Succeeded],
          ipgType: ipgTypes,
          fromDate: fromDateRange,
          toDate: toDateRange,
          cardNo,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      clientId,
      refId,
      ipgRefId,
      clientRefId,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(codes),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(ipgTypes),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(paymentStatuses),
      amount,
      pageNumber,
      pageSize,
      payerIdentity,
      payerName,
      fromDate,
      toDate,
      cardNo,
    ],
    { success: false, useProgress: true },
  );

  const data: TransactionRowType[] = (paymentList || []).map((item) => ({
    id: item.id,
    payDate: item.payDate,
    createdDate: item.createdDate,
    payerName: item.payerName,
    code: item.code,
    amount: item.amount,
    status: item.status,
    clientRefId: item.clientRefId,
    key: item.id,
  }));

  const cols: ColumnsType<TransactionRowType> = [
    {
      // مبلغ
      dataIndex: "amount",
      render(value) {
        return (
          <Flex justify="end" align="center">
            <Typography.Text>{formatLocalizedNumber({ value })}</Typography.Text>
            <CuToman width={24} height={24} />
          </Flex>
        );
      },
      title: t("report.table.amount"),
    },
    {
      // وضعیت پرداخت
      dataIndex: "status",
      render(value) {
        const paymentStatusValue = getPaymentStatus(value);
        return <Tag color={paymentStatusValue.color}>{paymentStatusValue.name}</Tag>;
      },
      title: t("report.table.status"),
    },
    {
      // پرداخت کننده
      dataIndex: "payerName",
      ellipsis: true,
      title: t("report.table.name"),
    },
    {
      // کد پرداخت
      className: "en-font",
      dataIndex: "code",
      render(_, tableData) {
        return (
          <Copy showIcon text={tableData.code || ""}>
            <p className="tw:m-0! tw:max-w-27.5 tw:truncate">{tableData.code}</p>
          </Copy>
        );
      },
      title: t("report.table.code"),
    },
    {
      // شناسه مرجع سرویس
      className: "en-font",
      dataIndex: "clientRefId",
      render(_, tableData) {
        return (
          <Copy showIcon text={tableData.clientRefId || ""}>
            <p className="tw:m-0! tw:max-w-27.5 tw:truncate">{tableData.clientRefId}</p>
          </Copy>
        );
      },
      title: t("report.table.referenceId"),
    },
    {
      // تاریخ پرداخت
      dataIndex: "payDate",
      render: (value) => toJalali({ date: getCorrectDate(value), toFormat: dateFormats.ui.jalaliDateTime }).string,
      title: t("report.table.date"),
    },
    {
      // جزییات
      title: t("common.operations"),
      key: "action",
      render: (_, record) => (
        <Typography.Link
          onClick={() => {
            setDetailPaymentCode(record.code);
          }}
          key={record.code}
        >
          <TableAction label={t("common.detail")} />
        </Typography.Link>
      ),
    },
  ];

  return (
    <Flex vertical className={"tw:w-full"} gap={16}>
      {/* < تایتل صفحه: آخرین پرداخت ها - فیلتر فرم ها ( جستوجو - فیلترها - دانلود فایل ) > */}
      <TransactionFilter retry={retry} loading={loading} />

      {/* < جدول تراکنش ها  > */}
      <Table
        loading={loading}
        hasIdColumn
        dataSource={data}
        size={"large"}
        columns={cols}
        pagination={{
          onChange: (page, newSize): void => {
            const newValues = {
              ...parsedSearch,
              pageNumber: page,
              pageSize: newSize,
            };
            submitQueryParams({
              navigate,
              newValues,
              parsedSearch,
              pathname: location.pathname,
              retry,
              stringifyOptions: { arrayFormat: "bracket" },
            });
          },
          onShowSizeChange: (_currentPage, newSize) => {
            const newValues = {
              ...parsedSearch,
              pageNumber: 1,
              pageSize: newSize,
            };
            submitQueryParams({
              navigate,
              newValues,
              parsedSearch,
              pathname: location.pathname,
              retry,
              stringifyOptions: { arrayFormat: "bracket" },
            });
          },
          pageNumber: pageNumber,
          pageSize: pageSize,
          hasMore,
        }}
      />

      {/* < دراور مربوط به جزئیات در جدول > */}
      {detailPaymentCode ? (
        <TransactionDetails
          code={detailPaymentCode}
          open={!!detailPaymentCode}
          onClose={() => {
            setDetailPaymentCode(undefined);
          }}
        />
      ) : null}
    </Flex>
  );
};

export default ShowAllTransactionDetails;
